import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '../../componentsv2/Typography';
import Button, { ButtonColor } from '../../componentsv2/Button';
import ConfirmDialog from '../../componentsv2/ConfirmDialog';
import Tooltip from '../../componentsv2/Tooltip';
import { ArrowRightIcon } from '../../components/Icons';
import { useTranslation } from '../../hooks/i18n';
import { ACTIVATION } from '../../constants/auth';
import { COLORS } from '../../themev2';
import { Nullable } from '../../types';
import { useSnackbar } from '../../context';
import { discardDraftCompany, useAppDispatch } from '../../store';
import { Company, CompanyOnboardingStep, SatCredentialStatus } from '../../types/companyUser';

const MEXICO_COUNTRY_CODE = 'mx';

enum BannerVariant {
  Activate = 'activate',
  InProgress = 'inProgress',
}

const variantStyles: Record<
  BannerVariant,
  { border?: string; background: string; iconColor: string; buttonColor?: ButtonColor }
> = {
  [BannerVariant.Activate]: {
    border: '1px solid rgba(172, 131, 24, 0.30)',
    background: COLORS.orange[100],
    iconColor: COLORS.orange[900],
    buttonColor: 'orange',
  },
  [BannerVariant.InProgress]: {
    background: '#E0E4F2',
    iconColor: COLORS.indigo[500],
    buttonColor: 'secondary',
  },
};

// Once the onboarding is completed, the home shows the OnboardingTracker instead of this banner.
function getVariant(draftCompany: Nullable<Company>, companiesLength: number): BannerVariant | null {
  if (draftCompany === null && companiesLength === 0) return BannerVariant.Activate;
  if (draftCompany !== null && draftCompany.onboardingStep !== CompanyOnboardingStep.Completed) {
    return BannerVariant.InProgress;
  }
  return null;
}

interface ActivationBannerProps {
  draftCompany: Nullable<Company>;
  companiesLength: number;
}

const ActivationBanner = ({ draftCompany, companiesLength }: ActivationBannerProps) => {
  const { showSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['activation']);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const variant = getVariant(draftCompany, companiesLength);
  if (!variant) return null;
  const styles = variantStyles[variant];

  const handleButtonClick = () => {
    if (mxSatStatus === SatCredentialStatus.Invalid) {
      showSnackbar(t('activation:mxSat.invalidCredential'), 'error');
      navigate(ACTIVATION, { state: { satRfc: draftCompany?.taxId } });
    } else {
      navigate(ACTIVATION);
    }
  };

  // Lets the user drop a company they started by mistake; otherwise this banner would stay forever.
  const handleConfirmDiscard = async () => {
    if (!draftCompany) return;
    setIsDiscarding(true);
    try {
      await dispatch(discardDraftCompany(draftCompany.id)).unwrap();
      showSnackbar(t('activation:banner.discard.success'), 'success');
    } catch {
      showSnackbar(t('activation:banner.discard.error'), 'error');
    } finally {
      setIsDiscarding(false);
      setIsDiscardDialogOpen(false);
    }
  };

  const satCredentialStatus = draftCompany?.productStatus?.satCredentialStatus;
  const MX_SAT_OVERRIDES = [SatCredentialStatus.Pending, SatCredentialStatus.Invalid] as const;
  type MxSatOverride = typeof MX_SAT_OVERRIDES[number];
  const mxSatStatus: MxSatOverride | null =
    draftCompany?.country === MEXICO_COUNTRY_CODE &&
    MX_SAT_OVERRIDES.includes(satCredentialStatus as MxSatOverride)
      ? (satCredentialStatus as MxSatOverride)
      : null;

  const handleContent = (): string => {
    let key: string = variant;
    if (mxSatStatus) {
      key = `mxSat.${mxSatStatus}`;
    }
    return key;
  };

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          display: 'flex',
          padding: '24px',
          alignItems: 'center',
          gap: '24px',
          alignSelf: 'stretch',
          borderRadius: '12px',
          border: styles.border,
          background: styles.background,
        }}
      >
        <InfoOutlinedIcon sx={{ color: styles.iconColor, flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: COLORS.gray[1200] }}
          >
            {t(`activation:banner.${variant}.title`)}
          </Typography>
          <Typography
            sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: COLORS.gray[1100] }}
          >
            {t(`activation:banner.${handleContent()}.subtitle`)}
          </Typography>
        </Box>
        <Button onClick={handleButtonClick} endIcon={<ArrowRightIcon />} color={styles.buttonColor}>
          {t(`activation:banner.${variant}.button`)}
        </Button>
        {variant === BannerVariant.InProgress && (
          <Tooltip title={t('activation:banner.discard.closeLabel')}>
            <IconButton
              aria-label={t('activation:banner.discard.closeLabel')}
              onClick={() => setIsDiscardDialogOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              <CloseIcon sx={{ color: COLORS.gray[1100] }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <ConfirmDialog
        open={isDiscardDialogOpen}
        title={t('activation:banner.discard.title')}
        content={t('activation:banner.discard.content')}
        confirmLabel={t('activation:banner.discard.confirm')}
        cancelLabel={t('activation:banner.discard.cancel')}
        confirmColor="danger"
        isLoading={isDiscarding}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setIsDiscardDialogOpen(false)}
      />
    </Grid>
  );
};

export default ActivationBanner;
